/**
 * One global value ladder shared by every slot of every pack. Endpoints are
 * multiples of pack price and are contiguous, so a rarity name always denotes
 * the same *relative* payout no matter which pack produced it. Packs differ
 * only in how they weight the ladder — `calibration()` then scales the whole
 * ladder per pack so realised EV lands exactly on that pack's published RTP.
 */
export const LADDER = {
    common: { lo: 0.02, hi: 0.08 },
    uncommon: { lo: 0.08, hi: 0.6 },
    rare: { lo: 0.6, hi: 2.5 },
    epic: { lo: 2.5, hi: 12 },
    legendary: { lo: 12, hi: 60 },
    mythic: { lo: 60, hi: 600 },
};
/** Weights are per 10,000 draws, so they read directly as basis points. */
function bands(weights) {
    return Object.entries(weights).map(([rarity, weight]) => ({
        rarity: rarity,
        weight: weight,
        ...LADDER[rarity],
    }));
}
/** Almost always a common, with just enough upside to be worth reading. */
const FILLER = {
    label: 'Filler',
    bands: bands({ common: 9300, uncommon: 655, rare: 43, epic: 2 }),
};
/** Guaranteed rare-or-better. Carries the large majority of a pack's EV. */
const HIT = {
    label: 'Hit',
    bands: bands({ rare: 8650, epic: 1150, legendary: 185, mythic: 15 }),
};
/** Same floor, longer tail — mid payouts sold off to buy a fatter mythic. */
const HIT_VOLATILE = {
    label: 'Hit',
    bands: bands({ rare: 9080, epic: 800, legendary: 100, mythic: 20 }),
};
/**
  * Ordered by price, which is the order the shop renders in and the order
  * `catalogue()` returns. Silver and Gold sit in what were the two widest gaps
  * on the ladder ($50 -> $250 and $250 -> $1,000), so the metals still read
  * Silver < Gold < Diamond even though Elite happens to price between them.
  */
export const TIERS = [
    'starter',
    'pro',
    'silver',
    'elite',
    'gold',
    'diamond',
];
/* ------------------------------------------------------------------ *
 * Card count
 * ------------------------------------------------------------------ */
export const MIN_CARDS = 3;
export const MAX_CARDS = 10;
/**
 * The shortest pack, so a tier's headline price is the price you actually pay
 * at the default: Starter *is* $25. Every card the slider adds is an explicit,
 * priced upgrade rather than a default the shop quietly charged for.
 */
export const DEFAULT_CARDS = MIN_CARDS;
/**
 * What each card count costs, as a multiple of the tier headline price.
 *
 * Deliberately super-linear: ten cards costs 4x a three-card pack while
 * carrying 3.33x the cards, so the stake riding on each individual card climbs
 * about 20% across the slider. RTP is fixed per tier and `calibration()` scales
 * the ladder to whatever the price ends up being, so that extra stake comes
 * straight back out as bigger payouts rather than as a worse deal. A long pack
 * is a higher-variance version of the same product, not a more expensive one.
 *
 * Every value is a multiple of 0.04, which is what keeps each price on each
 * tier a whole number of dollars instead of $32.50.
 */
export const CARD_MULTIPLIER = {
    3: 1.0,
    4: 1.32,
    5: 1.68,
    6: 2.04,
    7: 2.44,
    8: 2.88,
    9: 3.4,
    10: 4.0,
};
export function clampCards(cards) {
    if (!Number.isFinite(cards))
        return DEFAULT_CARDS;
    return Math.min(MAX_CARDS, Math.max(MIN_CARDS, Math.round(cards)));
}
/**
 * Guaranteed rare-or-better slots at a given length.
 *
 * This changes shape, never EV: calibration pins expected value to the
 * published RTP whatever the slot mix is. What it buys is that a ten-card pack
 * does not read as nine duds and one hit — the guaranteed value is spread over
 * three moments instead of one.
 */
export function hitsFor(cards) {
    return Math.max(1, Math.round(clampCards(cards) / 4));
}
/**
 * Four tiers, and RTP climbs with stake — the same shape the physical pack
 * market uses. Within a tier RTP is flat across every card count, so the slider
 * changes how the money is spread, never how much of it comes back.
 */
const TIER_SPECS = [
    {
        tier: 'starter',
        basePrice: 25,
        rtp: 0.54,
        accent: '#30F810',
        pitch: 'Robinhood Chain tokens, BTC, SOL and tokenized metals. The entry ticket — one guaranteed hit, every rip.',
    },
    {
        tier: 'pro',
        basePrice: 50,
        rtp: 0.57,
        accent: '#3B82F6',
        pitch: 'Twice the stake riding on every card, and a better published RTP.',
    },
    {
        tier: 'silver',
        basePrice: 100,
        rtp: 0.585,
        // Cooler and a stop darker than Diamond's #E7EDF5 on purpose. Two
        // near-white tiers three rungs apart are the one pair on this ladder a
        // buyer could genuinely confuse at tile size.
        accent: '#9DAFC4',
        pitch: 'The first tier where a bad rip still tends to leave something behind.',
    },
    {
        tier: 'elite',
        basePrice: 250,
        rtp: 0.6,
        accent: '#A855F7',
        volatile: true,
        pitch: 'Serious stakes, a thinner middle, and a mythic band that actually opens.',
    },
    {
        tier: 'gold',
        basePrice: 500,
        rtp: 0.615,
        accent: '#F2A900',
        volatile: true,
        pitch: 'Second only to Diamond, and the cheapest way into the long tail.',
    },
    {
        tier: 'diamond',
        basePrice: 1000,
        rtp: 0.63,
        accent: '#E7EDF5',
        volatile: true,
        pitch: 'Top of the ladder. Highest RTP on the site and the fattest tail.',
    },
];
const SPEC_BY_TIER = new Map(TIER_SPECS.map((s) => [s.tier, s]));
export const TIER_LABEL = {
    starter: 'Starter',
    pro: 'Pro',
    silver: 'Silver',
    elite: 'Elite',
    gold: 'Gold',
    diamond: 'Diamond',
};
export const TIER_ACCENT = Object.fromEntries(TIER_SPECS.map((s) => [s.tier, s.accent]));
/** USD, always a whole number of dollars. */
export function priceFor(tier, cards) {
    const spec = SPEC_BY_TIER.get(tier);
    if (!spec)
        throw new Error(`unknown tier: ${tier}`);
    const multiplier = CARD_MULTIPLIER[clampCards(cards)] ?? 1;
    return Math.round(spec.basePrice * multiplier * 100) / 100;
}
/** Every price this tier can be bought at, keyed by card count. */
export function priceTable(tier) {
    const table = {};
    for (let cards = MIN_CARDS; cards <= MAX_CARDS; cards++)
        table[cards] = priceFor(tier, cards);
    return table;
}
/**
 * Ids encode the whole configuration, so a pack is addressable by URL and the
 * open endpoint needs no request body: `elite-7` is one pack, unambiguously.
 * Historical `Open` rows keep whatever id they were booked under, which is
 * exactly why price and name are stored on the row as well.
 */
export function packId({ tier, cards }) {
    return `${tier}-${clampCards(cards)}`;
}
export function parsePackId(id) {
    const match = /^([a-z]+)-(\d+)$/.exec(id);
    if (!match)
        return undefined;
    const tier = match[1];
    if (!SPEC_BY_TIER.has(tier))
        return undefined;
    const cards = Number(match[2]);
    if (cards < MIN_CARDS || cards > MAX_CARDS)
        return undefined;
    return { tier, cards };
}
function slotsFor(spec, cards) {
    const hits = hitsFor(cards);
    const hit = spec.volatile ? HIT_VOLATILE : HIT;
    return [
        ...Array.from({ length: cards - hits }, () => FILLER),
        ...Array.from({ length: hits }, () => hit),
    ];
}
/** Builds the full pack for one configuration. The only constructor. */
export function resolvePack(config) {
    const spec = SPEC_BY_TIER.get(config.tier);
    if (!spec)
        return undefined;
    const cards = clampCards(config.cards);
    return {
        id: packId({ ...config, cards }),
        name: `${TIER_LABEL[spec.tier]} Pack`,
        tagline: spec.pitch,
        price: priceFor(spec.tier, cards),
        basePrice: spec.basePrice,
        rtp: spec.rtp,
        tier: spec.tier,
        accent: spec.accent,
        slots: slotsFor(spec, cards),
        // Every filename carries a version, and it is bumped whenever the print
        // changes. `/art/` is cached 30 days with no content hash in the path, so
        // re-writing a file at the same URL leaves it stale in Cloudflare's edge
        // and in every visitor's browser for up to a month with no way to force a
        // re-fetch. A new filename is a guaranteed cache miss everywhere, no
        // purge required.
        //
        // v4: the printed footer moved from ELYXR.XYZ to ELYXR.GG with the domain
        // change. The domain is baked into the JPEG, so a rename is the only way
        // the new print reaches anyone already holding a cached copy. Re-run
        // `node apps/web/scripts/pack-art.mjs` after bumping — it writes to
        // whatever this says.
        art: `/art/pack-${spec.tier}-v4.jpg`,
    };
}
export function getPack(id) {
    const config = parsePackId(id);
    return config && resolvePack(config);
}
/**
 * The shop. Four packs, each shown at one configuration — the slider on the
 * pack page is what turns those four into the full ladder.
 */
export function catalogue(cards = DEFAULT_CARDS) {
    return TIERS.map((tier) => resolvePack({ tier, cards }));
}
/** Every configuration that exists. Used by the impact audit and the tests. */
export function allConfigurations() {
    const packs = [];
    for (const tier of TIERS) {
        for (let cards = MIN_CARDS; cards <= MAX_CARDS; cards++) {
            packs.push(resolvePack({ tier, cards }));
        }
    }
    return packs;
}
//# sourceMappingURL=packs.js.map